const apiUrl = 'api/azure-table-operations';
import { useQuery } from '@tanstack/react-query';
import { ITenantUser } from '../../models/tenantUser';
import { BatchHistory } from '../query/batch';
import { JobStatus } from '../../models/jobStatus';
import axiosInstance from '../axios/axios';

export type OperationType =
  | 'get'
  | 'getall'
  | 'delete'
  | 'getallentity'
  | 'insert'
  | 'replace'
  | 'getbyquery'
  | 'getusagereportbyquery'
  | 'bulkinsert';

interface TableOperationRequest {
  tableName: string;
  operationName: OperationType;
  partitionKey?: string;
  rowKey?: string;
  entity?: unknown;
  query?: string;
}

export interface BulkEntityData {
  partitionKey: string;
  rowKey: string;
  entity: any;
  isUpdate: boolean;
}

export interface FailureDetail {
  entity: BulkEntityData;
  error: string;
}

export interface BulkOperationResult {
  successCount: number;
  updateCount: number;
  failureCount: number;
  failures: FailureDetail[];
}

export const addEntity = async (
  tableName: string,
  entity: any,
): Promise<any> => {
  const { partitionKey, rowKey, ...restEntity } = entity;
  try {
    const request: TableOperationRequest = {
      tableName,
      operationName: 'replace',
      partitionKey: partitionKey,
      rowKey: rowKey,
      entity: {
        ...restEntity,
      },
    };

    const response = await axiosInstance.post(apiUrl, request);
    return response.headers;
  } catch (error) {
    console.error('Error adding entity:', error);
    throw error;
  }
};

export const getEntity = async (
  tableName: string,
  partitionKey: string,
  rowKey: string,
): Promise<any | null> => {
  try {
    const request: TableOperationRequest = {
      tableName: tableName,
      operationName: 'get',
      partitionKey,
      rowKey,
    };
    const response = await axiosInstance.post(apiUrl, request);
    return response.data;
  } catch (error) {
    console.error('Error fetching entity:', error);
    return null;
  }
};

export const getAllEntities = async (tableName: string): Promise<any> => {
  try {
    const request: TableOperationRequest = {
      tableName,
      operationName: 'getallentity',
    };
    const response = await axiosInstance.post(apiUrl, request);
    return response.data;
  } catch (error) {
    console.error('Error fetching entities:', error);
    return null;
  }
};

export const getFilteredEntities = async (
  tableName: string,
  partitionKey: string,
): Promise<any> => {
  try {
    const request: TableOperationRequest = {
      tableName,
      operationName: 'getall',
      partitionKey,
    };
    const response = await axiosInstance.post(apiUrl, request);
    return response.data;
  } catch (error) {
    console.error('Error fetching filtered entities:', error);
    return null;
  }
};

export const useGetFilteredEntities = (
  tableName: string,
  partitionKey: string,
) =>
  useQuery({
    queryKey: ['getFilteredEntities', tableName, partitionKey],
    queryFn: async () => getFilteredEntities(tableName, partitionKey),
  });

export const deleteEntity = async (
  tableName: string,
  partitionKey: string,
  rowKey: string,
): Promise<void> => {
  try {
    const request: TableOperationRequest = {
      tableName,
      operationName: 'delete',
      partitionKey,
      rowKey,
    };

    const response = await axiosInstance.post(apiUrl, request);
    return response.data;
  } catch (error) {
    console.error('Error deleting entity:', error);
    throw error;
  }
};

export const doesEntityExist = async (
  tableName: string,
  partitionKey: string,
  rowKey: string,
): Promise<boolean> => {
  try {
    const entity = await getEntity(tableName, partitionKey, rowKey);
    return !!entity;
  } catch (error) {
    console.error('Error checking if entity exists:', error);
    return false;
  }
};

export const updateEntity = async (
  tableName: string,
  entity: any,
): Promise<any> => {
  const { PartitionKey, RowKey, ...restEntity } = entity;

  try {
    const request: TableOperationRequest = {
      tableName,
      operationName: 'replace',
      partitionKey: PartitionKey,
      rowKey: RowKey,
      entity: {
        ...restEntity,
      },
    };

    const response = await axiosInstance.put(apiUrl, request);
    return response.data;
  } catch (error) {
    console.error('Error updating entity:', error);
    throw error;
  }
};

export const getByPartitionKey = async (
  tableName: string,
  partitionKey: string,
) => {
  try {
    const request: TableOperationRequest = {
      tableName,
      operationName: 'getall',
      partitionKey: partitionKey,
    };
    const response = await axiosInstance.post(apiUrl, request);
    if (response.status === 200) {
      return response.data as any[];
    } else {
      throw new Error(
        `Failed to fetch batch details. Status: ${response.status}`,
      );
    }
  } catch (error) {
    console.error('Error fetching batch details:', error);
    throw error;
  }
};

export const getTenantUser = async (tableName: string, email: string) => {
  try {
    const request: TableOperationRequest = {
      tableName,
      operationName: 'getbyquery',
      query: `RowKey eq '${email.toLowerCase()}'`,
    };
    const response = await axiosInstance.post(apiUrl, request);

    if (response.status === 200) {
      return response.data as ITenantUser[];
    } else {
      throw new Error(
        `Failed to fetch tenant user. Status: ${response.status}`,
      );
    }
  } catch (error) {
    console.error('Error fetching tenant user:', error);
    throw error;
  }
};

export const useGetTenantUser = (tableName: string, email: string) =>
  useQuery({
    queryKey: ['getTenantUser', tableName, email],
    queryFn: () => getTenantUser(tableName, email),
  });

export const getBatchDetail = async (tenantId: string, batchId: string) => {
  try {
    const request: TableOperationRequest = {
      tableName: 'JobStatus',
      operationName: 'getbyquery',
      query: `PartitionKey eq '${tenantId}' and BatchId eq '${batchId.toLowerCase()}'`,
    };
    const response = await axiosInstance.post(apiUrl, request);
    if (response.status === 200) {
      return response.data as BatchHistory[];
    } else {
      throw new Error(
        `Failed to fetch batch details. Status: ${response.status}`,
      );
    }
  } catch (error) {
    console.error('Error fetching batch details:', error);
    throw error;
  }
};

export const getJobStatus = async (
  tenantId: string,
  batchId: string,
): Promise<JobStatus[]> => {
  try {
    const request: TableOperationRequest = {
      tableName: 'JobStatus',
      operationName: 'getbyquery',
      query: `PartitionKey eq '${tenantId}' and BatchId eq '${batchId.toLowerCase()}'`,
    };
    const response = await axiosInstance.post(apiUrl, request);
    return response.data as JobStatus[];
  } catch (error) {
    console.error(
      `Error fetching job status for tenantId: ${tenantId}, batchId: ${batchId}`,
      error,
    );
    throw error;
  }
};

export const checkTableForReference = async (
  tableName: string,
  partitionKey: string,
  CompanyId: string,
): Promise<boolean> => {
  try {
    const request: TableOperationRequest = {
      tableName,
      operationName: 'getbyquery',
      query: `PartitionKey eq '${partitionKey}' and CompanyId eq '${CompanyId}'`,
    };
    const response = await axiosInstance.post(apiUrl, request);
    return response.data.length > 0;
  } catch (error) {
    console.error(`Error checking ${tableName} for references:`, error);
    throw error;
  }
};

export const checkTableForVendorReference = async (
  tableName: string,
  VendorId: string,
): Promise<boolean> => {
  try {
    const request: TableOperationRequest = {
      tableName,
      operationName: 'getbyquery',
      query: `VendorId eq '${VendorId}'`,
    };
    const response = await axiosInstance.post(apiUrl, request);
    return response.data.length > 0;
  } catch (error) {
    console.error(`Error checking ${tableName} for references:`, error);
    throw error;
  }
};

export const addEntitiesBulk = async (
  tableName: string,
  entities: BulkEntityData[],
): Promise<BulkOperationResult> => {
  try {
    const request: TableOperationRequest = {
      tableName,
      operationName: 'bulkinsert',
      entity: entities,
    };

    const response = await axiosInstance.post(apiUrl, request);
    return response.data as BulkOperationResult;
  } catch (error) {
    console.error('Error performing bulk insert:', error);
    throw error;
  }
};
